import type { AppError } from './contract';

const defaultErrorCode = 'API_ERROR';
const defaultErrorMessage = 'The API request failed';

export async function normalizeApiError(response: Response): Promise<AppError> {
  const payload = await readJson(response);
  const envelope = isRecord(payload) && isRecord(payload['error']) ? payload['error'] : undefined;

  return createAppError({
    status: response.status,
    code: readString(envelope?.['code']) ?? defaultErrorCode,
    message: readString(envelope?.['message']) ?? defaultErrorMessage,
    requestId: readString(envelope?.['requestId']) ?? headerValue(response, 'x-request-id'),
    fieldErrors: toFieldErrors(envelope?.['details']),
    retryAfterSeconds: retryAfterSeconds(response),
  });
}

export function createAppError(error: AppError): AppError {
  return {
    status: error.status,
    code: error.code,
    message: error.message,
    requestId: error.requestId,
    fieldErrors: error.fieldErrors,
    retryAfterSeconds: error.retryAfterSeconds,
  };
}

export function transportError(): AppError {
  return createAppError({
    status: null,
    code: 'NETWORK_ERROR',
    message: 'Unable to reach the API',
    requestId: null,
    fieldErrors: {},
    retryAfterSeconds: null,
  });
}

function headerValue(response: Response, name: string): string | null {
  const value = response.headers.get(name)?.trim();
  return value === undefined || value.length === 0 ? null : value;
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return undefined;
  }
}

function toFieldErrors(details: unknown): Readonly<Record<string, string>> {
  if (!isRecord(details)) return {};

  const fieldErrors: Record<string, string> = {};
  for (const [field, message] of Object.entries(details)) {
    if (typeof message === 'string') fieldErrors[field] = message;
  }
  return fieldErrors;
}

function retryAfterSeconds(response: Response): number | null {
  const value = response.headers.get('retry-after')?.trim();
  if (value === undefined || value.length === 0 || !/^\d+$/u.test(value)) return null;

  const seconds = Number(value);
  return Number.isSafeInteger(seconds) ? seconds : null;
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}
